import type {
  CharacterIndexInput,
  CharacterIndexMentionType,
  CharacterIndexSourceAnchor
} from "./types.js";
import {
  mapValidatedWindowRangeToSourceAnchors,
  prepareSingleWitnessCharacterExtractionCall,
  validatePreparedExtractionCall,
  type CharacterExtractionScope,
  type CharacterExtractionSourceMapSpan,
  type PreparedCharacterExtractionScopeMetadata,
  type PreparedCharacterExtractionValidationIssue
} from "./extractionPreparation.js";
import type { CharacterMentionTextRange } from "./mentionGrounding.js";

export const SINGLE_WITNESS_COREFERENCE_CHAIN_EXTRACTION_PROMPT_VERSION =
  "single-witness-coreference-chain-extraction-v1";

export type CoreferenceChainPromptTextBuilder = (sourceText: string) => string;

export interface PrepareSingleWitnessCoreferenceChainExtractionOptions {
  readonly promptVersion?: string;
  readonly promptTextBuilder?: CoreferenceChainPromptTextBuilder;
}

export interface PreparedSingleWitnessCoreferenceChainExtractionCall {
  readonly promptText: string;
  readonly sourceText: string;
  readonly sourceMap: readonly CharacterExtractionSourceMapSpan[];
  readonly scope: PreparedCharacterExtractionScopeMetadata;
  readonly promptVersion: string;
}

export interface SingleWitnessCoreferenceChainOutput {
  readonly characters: readonly SingleWitnessCoreferenceChainCharacter[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainCharacter {
  readonly anchor: SingleWitnessCoreferenceChainAnchor;
  readonly chain: readonly SingleWitnessCoreferenceChainMention[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainAnchor {
  readonly name: string;
  readonly transliteration: string;
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainMention {
  readonly mention: string;
  readonly context: string;
  readonly host?: string | null;
  readonly [key: string]: unknown;
}

export type SingleWitnessCoreferenceChainValidationIssueCode =
  | "invalid-output"
  | "invalid-characters"
  | "invalid-character"
  | "invalid-anchor"
  | "invalid-chain"
  | "invalid-chain-mention"
  | "empty-anchor-name"
  | "empty-anchor-transliteration"
  | "empty-mention"
  | "empty-context";

export interface SingleWitnessCoreferenceChainValidationIssue {
  readonly code: SingleWitnessCoreferenceChainValidationIssueCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly characterIndex?: number;
  readonly mentionIndex?: number;
}

export type CoreferenceChainGroundingStatus = "grounded" | "ambiguous" | "not_found";

export type CoreferenceChainMentionMatchKind = "mention" | "host";

export type CoreferenceChainGroundingDiagnosticCode =
  | "empty-context"
  | "context-not-found"
  | "ambiguous-context"
  | "empty-mention"
  | "mention-not-found-in-context"
  | "ambiguous-mention";

export interface CoreferenceChainGroundingDiagnostic {
  readonly code: CoreferenceChainGroundingDiagnosticCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly characterIndex?: number;
  readonly mentionIndex?: number;
}

export interface GroundedSingleWitnessCoreferenceChainMention
  extends SingleWitnessCoreferenceChainMention {
  readonly status: CoreferenceChainGroundingStatus;
  readonly matchKind?: CoreferenceChainMentionMatchKind;
  readonly matchedText?: string;
  readonly contextRange?: CharacterMentionTextRange;
  readonly mentionRange?: CharacterMentionTextRange;
  readonly contextCandidateRanges: readonly CharacterMentionTextRange[];
  readonly mentionCandidateRanges: readonly CharacterMentionTextRange[];
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly CoreferenceChainGroundingDiagnostic[];
}

export interface GroundedSingleWitnessCoreferenceChainCharacter
  extends Omit<SingleWitnessCoreferenceChainCharacter, "anchor" | "chain"> {
  readonly status: CoreferenceChainGroundingStatus;
  readonly anchor: SingleWitnessCoreferenceChainAnchor;
  readonly chain: readonly GroundedSingleWitnessCoreferenceChainMention[];
  readonly diagnostics: readonly CoreferenceChainGroundingDiagnostic[];
}

export interface GroundedSingleWitnessCoreferenceChainOutput {
  readonly characters: readonly GroundedSingleWitnessCoreferenceChainCharacter[];
  readonly diagnostics: readonly CoreferenceChainGroundingDiagnostic[];
}

export interface CoreferenceChainSourceAnchorProjection extends CharacterIndexSourceAnchor {
  readonly mentionType: CharacterIndexMentionType;
}

export interface GroundSingleWitnessCoreferenceChainsOptions {
  readonly maxCandidateRanges?: number;
}

export const buildSingleWitnessCoreferenceChainPromptText = (
  sourceText: string
): string => [
  "# Coreference-chain extraction — classical Arabic witness",
  "",
  "## Goal",
  "For each character/agent (figure) in the text, whether human, animal, or otherwise personified, build its **coreference chain**: the ordered list of **every overt expression that refers to that figure** — proper names, epithets and definite descriptions, relative pronouns, referential demonstratives, free pronouns, and suffixed (bound) pronouns — each anchored to a verbatim context. One chain per figure.",
  "",
  "## Scope — what this task is and is not",
  "- This is **reference resolution only**: find every referring expression and group the ones that denote the same figure. Do **not** select or collate \"narrative units,\" do **not** apply any six-operation / aboutness test, do **not** classify figures. Those belong to other steps.",
  "- Read the text as a **single, self-contained witness**; use this text's words only; no knowledge of the wider work, other manuscripts, or the tradition.",
  "- **No canonicalization.** Record the witness's own expressions verbatim; never relabel a figure to a known/canonical name (no زعيم السباع → كليلة).",
  "",
  "## The atom: one chain per figure (deduplicated)",
  "Produce one entry per figure. Membership in a chain *is* the coreference claim: every expression in a chain is asserted to denote the same figure.",
  "",
  "- **Anchor** the chain by the figure's **first nominal mention** — its first non-pronoun, non-placeholder referring expression — copied verbatim. This identifies the chain without any \"representative-mention\" judgment.",
  "- A figure must have at least one non-placeholder nominal expression to anchor a chain. Do not build a chain out of pronouns alone.",
  "- **Under-merge when unsure.** If you cannot tell whether two expressions denote the same figure, keep them in separate chains rather than merging.",
  "",
  "## What counts as a mention (include — overt referring elements)",
  "1. **Proper names** — ظهره.",
  "2. **Definite descriptions, species terms, and epithets**, including species/role/profession/rank/kinship — الأسد، الثور، ابن آوى، الحمامة، الغراب، ابن الملك، التاجر، الأكّار، المرأة الشريفة، الجارية، الغلام، الفتى.",
  "3. **Relative pronouns used referentially** — الذي، التي (e.g., الذي هو من أبناء الملوك).",
  "4. **Referential demonstratives** — ذلك الغلام، تلك المرأة.",
  "5. **Free / independent pronouns**, including 1st and 2nd person inside direct speech — هو، هي، أنا، أنت، نحن.",
  "6. **Suffixed (bound) pronouns** — object and possessive: ـه، ـها، ـك، ـي … Record the **host word** verbatim (e.g., حسنه، رأيته، أرسلتني); the pronoun is the referring element within it.",
  "",
  "## What does NOT count",
  "- **Zero / null subjects carried only by verb agreement** — the dropped \"he\" in فقام / مضى / رجع, the \"I\" in كنت / هربت where no pronoun is written. **Excluded by default.** [Toggle: include them, represented by the governing verb verbatim. This is the largest and least reliable layer of the chain — turn it on only if you want full pro-drop coverage.]",
  "- **Placeholders** فلان / فلانة as anchors (a pronoun in the same clause that refers to a real figure is still a real mention of that figure and belongs in its chain).",
  "- **Place names** (مطور، قرنونا) and other non-agent entities.",
  "- **Generic species references that are not specific narrative participants**. Extract animals and other non-human agents when the text uses them as figures; do not extract an animal reference merely because it names a species.",
  "- The truncation marker `[...]`.",
  "",
  "## Coreference resolution (the core task)",
  "- **Link by figure-in-context, never by matching surface strings.** Generic epithets recur across **different** figures here: الفتى، الغلام، الرجل، and ابن الملك each denote more than one figure. In particular ابن الملك is both the protagonist (son of the king of قرنونا) and the city's enthroned heir (son of the dead local king) — **two figures, two chains**, even where the strings match. الجارية (the servant) is not المرأة / امرأة شريفة (the mistress).",
  "- **Track speaker and addressee through dialogue.** Inside direct speech, أنا and 1st-person forms refer to the current **speaker**; أنت and 2nd-person forms to the **addressee**. (E.g., أنا … واسمي ظهره → the speaker, the protagonist; the birds' أيّها العاقل العالم → the ascetic they address.) Reset speaker/addressee at each قال … boundary.",
  "- **Resolve each pronoun to the figure its context supports.** If a pronoun's antecedent is genuinely undecidable between figures, **leave it out** rather than guess. [Default — adjustable.]",
  "",
  "## Verbatim rule",
  "Every `mention` and every `context` is copied **exactly** from the source — same letters, diacritics (tashkīl), spelling, and word forms; no normalization, correction, or re-segmentation. For a suffixed pronoun, the `mention` is the host word, copied verbatim. If a `[...]` marker falls inside a `context`, end the span at the marker; never treat `[...]` as a figure.",
  "",
  "## Order",
  "List each chain's mentions in **textual order** of appearance.",
  "",
  "## IJMES (anchor)",
  "Transliterate the `anchor` name using IJMES conventions: ء → ʾ ; ع → ʿ ; ث → th ; ج → j ; ح → ḥ ; خ → kh ; ذ → dh ; ش → sh ; ص → ṣ ; ض → ḍ ; ط → ṭ ; ظ → ẓ ; غ → gh ; ق → q ; long vowels ā/ī/ū ; article **al-**, not assimilated to sun letters (al-tājir, not at-tājir) ; tāʾ marbūṭa → -a / -at ; transliterate as written, do not translate. [Per-mention transliteration of the whole chain is off by default — toggle on if needed.]",
  "",
  "## Output",
  "Return JSON only — no markdown fences, no preamble, no trailing text:",
  "",
  "```json",
  "{",
  "  \"characters\": [",
  "    {",
  "      \"anchor\": { \"name\": \"<first nominal mention, verbatim>\", \"transliteration\": \"<IJMES>\" },",
  "      \"chain\": [",
  "        { \"mention\": \"<verbatim referring expression>\", \"context\": \"<verbatim clause locating it>\" }",
  "      ]",
  "    }",
  "  ]",
  "}",
  "```",
  "",
  "- `chain` is ordered by textual appearance. Every mention — nominal or pronominal — carries a verbatim `context` long enough to make the intended referent recoverable (mandatory for pronouns, since a bare ـه or أنا is otherwise uninterpretable).",
  "",
  "## Example (abbreviated — the protagonist's chain)",
  "```json",
  "{",
  "  \"characters\": [",
  "    {",
  "      \"anchor\": { \"name\": \"أحدهم من أبناء الملوك\", \"transliteration\": \"aḥaduhum min abnāʾ al-mulūk\" },",
  "      \"chain\": [",
  "        { \"mention\": \"الذي هو من أبناء الملوك\", \"context\": \"قال الذي هو من أبناء الملوك إنّ أمر الدنيا كلّه وملاكها إنّما هو بالقدر\" },",
  "        { \"mention\": \"الفتى\", \"context\": \"فقال الفتى أنا ابن فلان الملك ملك قرنونا واسمي ظهره\" },",
  "        { \"mention\": \"أنا\", \"context\": \"أنا ابن فلان الملك ملك قرنونا واسمي ظهره\" },",
  "        { \"mention\": \"ظهره\", \"context\": \"واسمي ظهره كنت وُلّيت عهد أبي\" },",
  "        { \"mention\": \"الغلام\", \"context\": \"أرسل إلى الغلام فأخرجه من السجن\" }",
  "      ]",
  "    }",
  "  ]",
  "}",
  "```",
  "Notes on this chain: `أنا` is assigned to the protagonist because he is the speaker of that line (speaker tracking). `فلان` in the same clause is a placeholder, not a figure, so it is not a mention. The final `الغلام` is the protagonist (he was the one imprisoned) — *not* the enthroned heir, who is also called ابن الملك nearby; they are kept in separate chains. Zero subjects (e.g., the \"he\" in فأخرجه's surrounding verbs, the \"I\" in كنت) are omitted under the default; the suffixed ـه in فأخرجه would be included as a mention if it referred to the figure (here it does — him, the protagonist).",
  "",
  "---",
  "",
  "TEXT",
  sourceText
].join("\n");

export const buildPendingSingleWitnessCoreferenceChainPromptText =
  buildSingleWitnessCoreferenceChainPromptText;

export const prepareSingleWitnessCoreferenceChainExtractionCall = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  options: PrepareSingleWitnessCoreferenceChainExtractionOptions = {}
): PreparedSingleWitnessCoreferenceChainExtractionCall => {
  const sourceCall = prepareSingleWitnessCharacterExtractionCall(input, scope);
  const promptVersion =
    options.promptVersion || SINGLE_WITNESS_COREFERENCE_CHAIN_EXTRACTION_PROMPT_VERSION;
  const promptTextBuilder =
    options.promptTextBuilder || buildSingleWitnessCoreferenceChainPromptText;

  return {
    promptText: promptTextBuilder(sourceCall.sourceText),
    sourceText: sourceCall.sourceText,
    sourceMap: sourceCall.sourceMap,
    scope: sourceCall.scope,
    promptVersion
  };
};

export const validatePreparedCoreferenceChainExtractionCall = (
  call: PreparedSingleWitnessCoreferenceChainExtractionCall
): PreparedCharacterExtractionValidationIssue[] => validatePreparedExtractionCall(call);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

const isNonEmptyString = (value: unknown): value is string => (
  typeof value === "string" && value.trim().length > 0
);

const pushIssue = (
  issues: SingleWitnessCoreferenceChainValidationIssue[],
  issue: SingleWitnessCoreferenceChainValidationIssue
): void => {
  issues.push(issue);
};

const validateMentionShape = (
  value: unknown,
  issues: SingleWitnessCoreferenceChainValidationIssue[],
  path: readonly (string | number)[],
  characterIndex: number,
  mentionIndex: number | undefined,
  structuralCode: "invalid-anchor" | "invalid-chain-mention"
): void => {
  if (!isRecord(value)) {
    pushIssue(issues, {
      code: structuralCode,
      message: "Coreference chain mention must be an object.",
      path,
      characterIndex,
      ...(mentionIndex !== undefined ? { mentionIndex } : {})
    });
    return;
  }

  if (!isNonEmptyString(value.mention)) {
    pushIssue(issues, {
      code: "empty-mention",
      message: "Coreference chain mention must include a non-empty mention string.",
      path: [...path, "mention"],
      characterIndex,
      ...(mentionIndex !== undefined ? { mentionIndex } : {})
    });
  }

  if (!isNonEmptyString(value.context)) {
    pushIssue(issues, {
      code: "empty-context",
      message: "Coreference chain mention must include a non-empty context string.",
      path: [...path, "context"],
      characterIndex,
      ...(mentionIndex !== undefined ? { mentionIndex } : {})
    });
  }
};

const validateAnchorShape = (
  value: unknown,
  issues: SingleWitnessCoreferenceChainValidationIssue[],
  path: readonly (string | number)[],
  characterIndex: number
): void => {
  if (!isRecord(value)) {
    pushIssue(issues, {
      code: "invalid-anchor",
      message: "Coreference chain anchor must be an object.",
      path,
      characterIndex
    });
    return;
  }

  if (!isNonEmptyString(value.name)) {
    pushIssue(issues, {
      code: "empty-anchor-name",
      message: "Coreference chain anchor must include a non-empty name string.",
      path: [...path, "name"],
      characterIndex
    });
  }

  if (!isNonEmptyString(value.transliteration)) {
    pushIssue(issues, {
      code: "empty-anchor-transliteration",
      message: "Coreference chain anchor must include a non-empty transliteration string.",
      path: [...path, "transliteration"],
      characterIndex
    });
  }
};

export const validateSingleWitnessCoreferenceChainOutput = (
  output: unknown
): SingleWitnessCoreferenceChainValidationIssue[] => {
  const issues: SingleWitnessCoreferenceChainValidationIssue[] = [];

  if (!isRecord(output)) {
    pushIssue(issues, {
      code: "invalid-output",
      message: "Coreference chain output must be an object.",
      path: []
    });
    return issues;
  }

  if (!Array.isArray(output.characters)) {
    pushIssue(issues, {
      code: "invalid-characters",
      message: "Coreference chain output must include a characters array.",
      path: ["characters"]
    });
    return issues;
  }

  output.characters.forEach((character, characterIndex) => {
    const characterPath = ["characters", characterIndex] as const;
    if (!isRecord(character)) {
      pushIssue(issues, {
        code: "invalid-character",
        message: "Coreference chain character must be an object.",
        path: characterPath,
        characterIndex
      });
      return;
    }

    validateAnchorShape(
      character.anchor,
      issues,
      [...characterPath, "anchor"],
      characterIndex
    );

    if (!Array.isArray(character.chain)) {
      pushIssue(issues, {
        code: "invalid-chain",
        message: "Coreference chain character must include a chain array.",
        path: [...characterPath, "chain"],
        characterIndex
      });
      return;
    }

    character.chain.forEach((mention, mentionIndex) => {
      validateMentionShape(
        mention,
        issues,
        [...characterPath, "chain", mentionIndex],
        characterIndex,
        mentionIndex,
        "invalid-chain-mention"
      );
    });
  });

  return issues;
};

const findExactRanges = (
  sourceText: string,
  text: string
): CharacterMentionTextRange[] => {
  if (!text) {
    return [];
  }

  const ranges: CharacterMentionTextRange[] = [];
  let cursor = 0;
  while (cursor <= sourceText.length) {
    const start = sourceText.indexOf(text, cursor);
    if (start < 0) {
      break;
    }
    const end = start + text.length;
    ranges.push({ start, end });
    cursor = start + 1;
  }
  return ranges;
};

const limitCandidateRanges = (
  ranges: readonly CharacterMentionTextRange[],
  options: GroundSingleWitnessCoreferenceChainsOptions
): readonly CharacterMentionTextRange[] => {
  if (typeof options.maxCandidateRanges !== "number") {
    return ranges;
  }
  return ranges.slice(0, Math.max(0, Math.floor(options.maxCandidateRanges)));
};

const toAbsoluteRanges = (
  contextRange: CharacterMentionTextRange,
  relativeRanges: readonly CharacterMentionTextRange[]
): CharacterMentionTextRange[] => (
  relativeRanges.map((range) => ({
    start: contextRange.start + range.start,
    end: contextRange.start + range.end
  }))
);

const diagnostic = (
  code: CoreferenceChainGroundingDiagnosticCode,
  message: string,
  path: readonly (string | number)[],
  characterIndex: number,
  mentionIndex?: number
): CoreferenceChainGroundingDiagnostic => ({
  code,
  message,
  path,
  characterIndex,
  ...(mentionIndex !== undefined ? { mentionIndex } : {})
});

const groundCoreferenceMention = (
  call: Pick<PreparedSingleWitnessCoreferenceChainExtractionCall, "sourceText" | "sourceMap">,
  mention: SingleWitnessCoreferenceChainMention,
  path: readonly (string | number)[],
  characterIndex: number,
  mentionIndex: number | undefined,
  options: GroundSingleWitnessCoreferenceChainsOptions
): GroundedSingleWitnessCoreferenceChainMention => {
  if (mention.context.trim().length === 0) {
    return {
      ...mention,
      status: "not_found",
      contextCandidateRanges: [],
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic("empty-context", "Coreference mention context is empty.", path, characterIndex, mentionIndex)
      ]
    };
  }

  const contextRanges = findExactRanges(call.sourceText, mention.context);
  const contextCandidateRanges = limitCandidateRanges(contextRanges, options);
  if (contextRanges.length === 0) {
    return {
      ...mention,
      status: "not_found",
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "context-not-found",
          "Coreference mention context does not occur in the prepared sourceText.",
          path,
          characterIndex,
          mentionIndex
        )
      ]
    };
  }

  if (contextRanges.length > 1) {
    return {
      ...mention,
      status: "ambiguous",
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "ambiguous-context",
          "Coreference mention context occurs more than once in the prepared sourceText.",
          path,
          characterIndex,
          mentionIndex
        )
      ]
    };
  }

  const contextRange = contextRanges[0]!;
  const contextText = call.sourceText.slice(contextRange.start, contextRange.end);
  const contextSourceAnchors = mapValidatedWindowRangeToSourceAnchors(call, contextRange);
  const host = typeof mention.host === "string" && mention.host.trim().length > 0
    ? mention.host
    : null;
  const matchText = host || mention.mention;
  const matchKind: CoreferenceChainMentionMatchKind = host ? "host" : "mention";

  if (matchText.trim().length === 0) {
    return {
      ...mention,
      status: "not_found",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic("empty-mention", "Coreference mention text is empty.", path, characterIndex, mentionIndex)
      ]
    };
  }

  let relativeMentionRanges = findExactRanges(contextText, matchText);
  let resolvedMatchText = matchText;
  let resolvedMatchKind = matchKind;

  if (relativeMentionRanges.length === 0 && host) {
    relativeMentionRanges = findExactRanges(contextText, mention.mention);
    resolvedMatchText = mention.mention;
    resolvedMatchKind = "mention";
  }

  const mentionRanges = toAbsoluteRanges(contextRange, relativeMentionRanges);
  const mentionCandidateRanges = limitCandidateRanges(mentionRanges, options);
  if (mentionRanges.length === 0) {
    return {
      ...mention,
      status: "not_found",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges,
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "mention-not-found-in-context",
          "Coreference mention does not occur inside its grounded context.",
          path,
          characterIndex,
          mentionIndex
        )
      ]
    };
  }

  if (mentionRanges.length > 1) {
    return {
      ...mention,
      status: "ambiguous",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges,
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "ambiguous-mention",
          "Coreference mention occurs more than once inside its grounded context.",
          path,
          characterIndex,
          mentionIndex
        )
      ]
    };
  }

  const mentionRange = mentionRanges[0]!;
  return {
    ...mention,
    status: "grounded",
    matchKind: resolvedMatchKind,
    matchedText: resolvedMatchText,
    contextRange,
    mentionRange,
    contextCandidateRanges,
    mentionCandidateRanges,
    contextSourceAnchors,
    sourceAnchors: mapValidatedWindowRangeToSourceAnchors(call, mentionRange),
    diagnostics: []
  };
};

const combineStatuses = (
  occurrences: readonly GroundedSingleWitnessCoreferenceChainMention[]
): CoreferenceChainGroundingStatus => {
  if (occurrences.some((occurrence) => occurrence.status === "not_found")) {
    return "not_found";
  }
  if (occurrences.some((occurrence) => occurrence.status === "ambiguous")) {
    return "ambiguous";
  }
  return "grounded";
};

export const groundSingleWitnessCoreferenceChains = (
  call: Pick<PreparedSingleWitnessCoreferenceChainExtractionCall, "sourceText" | "sourceMap">,
  output: SingleWitnessCoreferenceChainOutput,
  options: GroundSingleWitnessCoreferenceChainsOptions = {}
): GroundedSingleWitnessCoreferenceChainOutput => {
  const characters = output.characters.map((character, characterIndex) => {
    const { anchor, chain, ...futureFields } = character;
    const groundedChain = chain.map((mention, mentionIndex) => (
      groundCoreferenceMention(
        call,
        mention,
        ["characters", characterIndex, "chain", mentionIndex],
        characterIndex,
        mentionIndex,
        options
      )
    ));
    const diagnostics = groundedChain.flatMap((mention) => mention.diagnostics);

    return {
      ...futureFields,
      status: combineStatuses(groundedChain),
      anchor,
      chain: groundedChain,
      diagnostics
    };
  });

  return {
    characters,
    diagnostics: characters.flatMap((character) => character.diagnostics)
  };
};

const isGroundedAnchorMention = (
  character: GroundedSingleWitnessCoreferenceChainCharacter,
  mention: GroundedSingleWitnessCoreferenceChainMention
): boolean => (
  mention.status === "grounded" &&
  (mention.mention === character.anchor.name || mention.matchedText === character.anchor.name)
);

export const projectGroundedCoreferenceChainsToSourceAnchors = (
  grounded: GroundedSingleWitnessCoreferenceChainOutput
): CoreferenceChainSourceAnchorProjection[] => (
  grounded.characters.flatMap((character) => {
    let projectedAnchorMention = false;

    return character.chain.flatMap((mention) => {
      if (mention.status !== "grounded" || mention.sourceAnchors.length === 0) {
        return [];
      }

      const mentionType: CharacterIndexMentionType =
        !projectedAnchorMention && isGroundedAnchorMention(character, mention)
          ? "name"
          : "coref";
      if (mentionType === "name") {
        projectedAnchorMention = true;
      }

      return mention.sourceAnchors.map((sourceAnchor) => ({
        ...sourceAnchor,
        mentionType
      }));
    });
  })
);
